import React, { useEffect, useState, useCallback } from "react";
import { Badge, Drawer, Tooltip } from "antd";
import { api } from "@redux/api";
import { useSelector } from "react-redux";
import { WorkerCommentTooltip } from "@containers/facility/Workorder/WorkorderTooltip";
import { WorkorderComment } from "@containers/facility/Workorder/WorkorderComment";
import { EventName, useEventBus } from "@/hooks/useEventBus";

const MOBILE_COMMENT_BREAKPOINT = 768;

const useIsMobileComment = () => {
  const getIsMobile = useCallback(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.innerWidth < MOBILE_COMMENT_BREAKPOINT;
  }, []);
  const [isMobile, setIsMobile] = useState(getIsMobile);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleResize = () => {
      setIsMobile(getIsMobile());
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [getIsMobile]);

  return isMobile;
};

const WorkorderNotification = React.memo(({ record }) => {

  const [notifications, setNotifications] = useState({
    unreadIds: [], // Changed from unread count to ID array
    read: record.read_notifications_count || 0,
    initialized: false,
  });
  const [hasComments, setHasComments] = useState(false);

  const drawer = useSelector((state) => state.EditableTable.drawer);
  const drawer_id = useSelector((state) => state.EditableTable.id);
  const [open, setOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const isMobileComment = useIsMobileComment();

  useEventBus(EventName.RECIVED_WORKORDER_MESSAGE, (data) => {
    if (record.id === data.parent_id) {
      if (data.type === 'kpiworkordercomment_created') {
        if (!open) {
          setNotifications((prev) => {
            // Use comment_id if available, fallback to id
            const commentId = data.comment_id || data.id;
            const newUnreadIds = prev.unreadIds.includes(commentId) ? prev.unreadIds : [...prev.unreadIds, commentId];
            return {
              unreadIds: newUnreadIds,
              read: prev.read,
            };
          });
        }
        // 有新消息时，设置 hasComments 为 true
        setHasComments(true);
      } else if (data.type === 'kpiworkordercomment_deleted') {
        // Use comment_id field (backend now provides this correctly)
        const commentIdToDelete = data.comment_id || data.id;
        
        // Remove the deleted comment ID from unread list
        setNotifications((prev) => ({
          unreadIds: prev.unreadIds.filter(id => id !== commentIdToDelete),
          read: prev.read,
        }));
      }
    }
  });

  const setReadComment = useCallback(() => {
    let isMounted = true;
    api
      .updateWorkorderComment(record.id, { status: "read" })
      .then(() => {
        if (isMounted) {
          setNotifications((prev) => ({
            unreadIds: [],
            read: prev.read + prev.unreadIds.length,
          }));
        }
      })
      .catch((error) =>
        console.error("Error updating notification status:", error)
      );
    return () => {
      isMounted = false;
    };
  }, [record.id]);

  useEffect(() => {
    if (record.id === drawer_id && drawer === true) {
      const cleanup = setReadComment();
      return cleanup;
    }
  }, [drawer, drawer_id, record.id, setReadComment]);

  useEffect(() => {
    let isSubscribed = true;

    const fetchData = async () => {
      try {
        const response = await api.getWorkorderComment(record.id);
        if (isSubscribed) {
          // Extract unread comment IDs from response
          const unreadIds = response.data.results
            ? response.data.results
                .filter(comment => comment.status === 'unread')
                .map(comment => comment.id)
            : [];
          
          setNotifications((prev) => ({
            unreadIds: unreadIds,
            read:
              response.data.read_notifications_count ??
              record.read_notifications_count ??
              0,
            initialized: true,
          }));
          // 检查是否有 comments 数据
          const hasCommentsData = response.data.results && response.data.results.length > 0;
          setHasComments(hasCommentsData);
        }
      } catch (error) {
        console.error("Error fetching workorder comment:", error);
        if (isSubscribed) {
          setNotifications((prev) => ({
            unreadIds: [],
            read: record.read_notifications_count || 0,
            initialized: true,
          }));
          setHasComments(false);
        }
      } finally {

      }
    };

    fetchData();

    return () => {
      isSubscribed = false;
    };
  }, [
    record.id,
    record.unread_notifications_count,
    record.read_notifications_count,
  ]);

  const handleTooltipOpenChange = useCallback(
    (open) => {
      setOpen(open);
      if (open) {
        setReadComment();
      }
    },
    [setReadComment]
  );

  const commentTrigger = hasComments ? (
    <Badge
      count={notifications.unreadIds.length}
      size="small"
      style={{ cursor: "pointer" }}
    >
      <i
        className="fas fa-comment-dots"
        style={{
          color: "#faad14",
          fontSize: "18px",
          cursor: "pointer",
        }}
      />
    </Badge>
  ) : (
    <Badge size="small">
      <i
        className="fa-regular fa-comment-dots"
        style={{
          color: "grey",
          fontSize: "18px",
          cursor: "pointer",
        }}
      />
    </Badge>
  );

  if (isMobileComment) {
    const handleMobileOpen = () => {
      setDrawerOpen(true);
      setOpen(true);
      setReadComment();
    };

    const handleMobileClose = () => {
      setDrawerOpen(false);
      setOpen(false);
    };

    return (
      <>
        <span onClick={handleMobileOpen}>{commentTrigger}</span>
        <Drawer
          title="Comments"
          placement="bottom"
          height="85vh"
          open={drawerOpen}
          onClose={handleMobileClose}
          destroyOnClose={true}
          className="workorder-comment-mobile-drawer"
          bodyStyle={{ padding: 0 }}
        >
          <WorkorderComment
            id={record.id}
            boxHeight="calc(85vh - 56px)"
            equipmentId={record.equipments?.[0]}
          />
        </Drawer>
      </>
    );
  }

  return (
    <Tooltip
      placement="topLeft"
      trigger={["click"]}
      overlayStyle={{
        maxWidth: "800px",
        padding: 0,
        marginLeft: "-20px",
      }}
      overlayInnerStyle={{
        padding: 0,
        borderRadius: "8px",
        overflow: "hidden",
      }}
      onOpenChange={handleTooltipOpenChange}
      title={WorkerCommentTooltip(record.id, record.equipments?.[0])}
      color="#fff"
      destroyTooltipOnHide={true}
    >
      {commentTrigger}
    </Tooltip>
  );
});

export default WorkorderNotification;
